import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarEvent, EventType } from './Calendar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Calendar as DatePicker } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { useIsMobile } from '@/hooks/use-mobile';

interface EventFormProps {
  date: Date;
  onSubmit: (event: Omit<CalendarEvent, 'id'>) => void;
  onCancel: () => void;
}

export function EventForm({ date, onSubmit, onCancel }: EventFormProps) {
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState<Date>(date);
  const [eventType, setEventType] = useState<EventType>('task');
  const [reminder, setReminder] = useState(false);
  const [reminderTime, setReminderTime] = useState<string>('');
  const [reminderText, setReminderText] = useState<string>('');
  const [smsReminder, setSmsReminder] = useState(false);
  const [smsReminderTime, setSmsReminderTime] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const isMobile = useIsMobile();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formattedDate = format(eventDate, 'yyyy-MM-dd');
    onSubmit({
      title,
      date: formattedDate,
      type: eventType,
      reminder,
      reminderTime: reminder ? reminderTime : undefined,
      reminderText: reminderText || undefined,
      smsReminder,
      smsReminderTime: smsReminder ? smsReminderTime : undefined,
      phoneNumber: smsReminder ? phoneNumber : undefined,
    });
  };

  const eventTypes: Array<{ value: EventType; label: string }> = [
    { value: 'task', label: 'Task' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'personal', label: 'Personal' },
  ];

  const formatPhoneNumber = (value: string) => {
    const phoneDigits = value.replace(/\D/g, '');
    if (phoneDigits.length <= 3) {
      return phoneDigits;
    } else if (phoneDigits.length <= 6) {
      return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
    } else {
      return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-background rounded-lg p-4 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-4 relative">
        <Button 
          variant="ghost" 
          size="icon" 
          className="absolute right-2 top-2" 
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <h3 className="text-lg font-medium pr-8">Add Event</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !eventDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {eventDate ? format(eventDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <DatePicker
                  mode="single"
                  selected={eventDate}
                  onSelect={(date) => date && setEventDate(date)}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="type">Event Type</Label>
            <Select
              value={eventType}
              onValueChange={(value) => setEventType(value as EventType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent>
                {eventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="reminder"
              checked={reminder}
              onCheckedChange={setReminder}
            />
            <Label htmlFor="reminder">Set Reminder</Label>
          </div>
          
          {reminder && (
            <div className="space-y-2">
              <Label htmlFor="reminderTime">Reminder Time</Label>
              <Input
                id="reminderTime"
                type="datetime-local"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                required={reminder}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reminderText">Note</Label>
            <Textarea
              id="reminderText"
              value={reminderText}
              onChange={(e) => setReminderText(e.target.value)}
              placeholder="Add notes or details about this event"
              className="min-h-[80px] resize-y"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="smsReminder"
              checked={smsReminder}
              onCheckedChange={setSmsReminder}
            />
            <Label htmlFor="smsReminder">SMS Text Reminder</Label>
          </div>
          
          {smsReminder && (
            <>
              <div className="space-y-2">
                <Label htmlFor="smsReminderTime">SMS Reminder Time</Label>
                <Input
                  id="smsReminderTime"
                  type="datetime-local"
                  value={smsReminderTime}
                  onChange={(e) => setSmsReminderTime(e.target.value)}
                  required={smsReminder}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  required={smsReminder}
                />
                <p className="text-xs text-muted-foreground">
                  You'll receive a text reminder at this number
                </p>
              </div>
            </>
          )}
          
          <div className={cn(
            "flex justify-end gap-2 pt-2",
            isMobile ? "pb-16" : ""
          )}>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              Add Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
